import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import API, { UPLOADS_BASE_URL } from "../services/api";
import Loader from "../components/Loader";
import { 
    FaUser, 
    FaBriefcase, 
    FaUsers, 
    FaCamera, 
    FaArrowRight, 
    FaArrowLeft, 
    FaSave,
    FaHeart,
    FaArrowAltCircleLeft
} from "react-icons/fa";

function EditProfile({ viewOnly = false }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [previewImages, setPreviewImages] =
        useState([]);

    const [images, setImages] =
        useState([]);

    const [historyLogs, setHistoryLogs] = 
        useState([]);
    const [activeTab, setActiveTab] = useState("personal");

    const [form, setForm] = useState({
        // PERSONAL
        name: "",
        age: "",
        address: "",
        mobile: "",
        nativeAddress: "",
        birthday: "",
        anniversary: "",
        eatingHabits: "",
        alcoholHabits: "",
        socialMedia: "",
        politicalView: "",
        medicalCondition: "",

        // PROFESSIONAL
        department: "",
        reportingOfficer: "",
        officeTime: "",
        transferHistory: "",
        officePolitics: "",

        // FAMILY
        familyMembers: "",
        wife: "",
        brothers: "",
        sisters: "",
        mothers: "",
        fathers: "",
        fathersOccupation: "",
        occupation: "",
        studiedCity: "",
        children: "",
        childSchoolCollege: "",

        // ASSETS
        financial: "",
        vehicle: "",
        debt: "",

        // PASSIONS
        hobbies: "",
        dreams: "",
        wishes: ""
    });

    // =====================================
    // GET DEPARTMENTS
    // =====================================
    const getDepartments = async () => {
        try {
            const response = await API.get("/department/list");
            setDepartments(response.data.departments);
        } catch (error) {
            console.log(error);
        }
    };

    // =====================================
    // GET PROFILE
    // =====================================
    const getProfile = async () => {
        try {
            const response = await API.get(`/profile/details/${id}`);
            const profile = response.data.profile;

            setForm({
                // PERSONAL
                name: profile.personalDetails?.name || "",
                age: profile.personalDetails?.age || "",
                address: profile.personalDetails?.address || "",
                mobile: profile.personalDetails?.mobile || "",
                nativeAddress: profile.personalDetails?.nativeAddress || "",
                birthday: profile.personalDetails?.birthday || "",
                anniversary: profile.personalDetails?.anniversary || "",
                eatingHabits: profile.personalDetails?.eatingHabits || "",
                alcoholHabits: profile.personalDetails?.alcoholHabits || "",
                socialMedia: profile.personalDetails?.socialMedia || "",
                politicalView: profile.personalDetails?.politicalView || "",
                medicalCondition: profile.personalDetails?.medicalCondition || "",

                // PROFESSIONAL
                department: profile.professionalDetails?.department || "",
                reportingOfficer: profile.professionalDetails?.reportingOfficer || "",
                officeTime: profile.professionalDetails?.officeTime || "",
                transferHistory: profile.professionalDetails?.transferHistory || "",
                officePolitics: profile.professionalDetails?.officePolitics || "",

                // FAMILY
                familyMembers: profile.familyDetails?.familyMembers || "",
                wife: profile.familyDetails?.wife || "",
                brothers: profile.familyDetails?.brothers || "",
                sisters: profile.familyDetails?.sisters || "",
                mothers: profile.familyDetails?.mothers || "",
                fathers: profile.familyDetails?.fathers || "",
                fathersOccupation: profile.familyDetails?.fathersOccupation || "",
                occupation: profile.familyDetails?.occupation || "",
                studiedCity: profile.familyDetails?.studiedCity || "",
                children: profile.familyDetails?.children || "",
                childSchoolCollege: profile.familyDetails?.childSchoolCollege || "",

                // ASSETS
                financial: profile.assets?.financial || "",
                vehicle: profile.assets?.vehicle || "",
                debt: profile.assets?.debt || "",

                // PASSIONS
                hobbies: profile.passions?.hobbies || "",
                dreams: profile.passions?.dreams || "",
                wishes: profile.passions?.wishes || ""
            });

            // EXISTING IMAGES
            if (profile.images) {
                setPreviewImages(
                    profile.images.map((img) => `${UPLOADS_BASE_URL}/${img}`)
                );
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getHistory = async () => {
        try {
            const response = await API.get(`/log/target/${id}`);
            setHistoryLogs(response.data.logs);
        } catch (error) {
            console.error("Failed to fetch history logs", error);
        }
    };

    useEffect(() => {
        const loadAllData = async () => {
            try {
                setPageLoading(true);
                await Promise.all([
                    getDepartments(),
                    getProfile(),
                    localStorage.getItem("role") === "admin" ? getHistory() : Promise.resolve()
                ]);
            } catch (error) {
                console.error(error);
            } finally {
                setPageLoading(false);
            }
        };
        loadAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // =====================================
    // HANDLE CHANGE
    // =====================================
    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value
        });
    };

    // =====================================
    // HANDLE IMAGES
    // =====================================
    const handleImages = (e) => {
        const files = Array.from(e.target.files);
        setImages(files);

        const preview = files.map((file) => URL.createObjectURL(file));
        setPreviewImages(preview);
    };

    // =====================================
    // UPDATE PROFILE
    // =====================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim() || !form.mobile.trim() || !form.department.trim()) {
            alert("Name, Mobile & Department are required fields.");
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();

            // TEXT DATA
            for (let key in form) {
                formData.append(key, form[key]);
            }

            // IMAGES
            images.forEach((image) => {
                formData.append("images", image);
            });

            await API.put(`/profile/update/${id}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });

            alert("Profile Updated Successfully");
            navigate("/profiles");
        } catch (error) {
            alert(error.response?.data?.message || "Failed to update profile");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-wrapper">
            <Sidebar />

            <div className="content-wrapper">
                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-4 animate-fade-in">
                    <div>
                        <h2 className="fw-extrabold text-slate mb-1">
                            {viewOnly ? "View Profile" : "Edit Profile"}
                        </h2>
                        <p className="text-muted mb-0">
                            {viewOnly ? "Detailed view of employee record" : "Modify existing employee record"}
                        </p>
                    </div>
                    <button
                        className="btn btn-dark d-flex align-items-center gap-2"
                        onClick={() => navigate("/profiles")}
                    >
                        <FaArrowAltCircleLeft /> Back
                    </button>
                </div>

                {pageLoading ? (
                    <Loader message="Loading profile details..." />
                ) : (
                    <>
                        {/* TAB NAVIGATION */}
                        <div className="card border-0 shadow-sm mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                    <div className="card-body p-2">
                        <ul className="nav nav-pills nav-pills-custom d-flex flex-nowrap overflow-auto justify-content-start justify-content-md-between gap-2 border-0">
                            <li className="nav-item flex-fill">
                                <button
                                    className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === "personal" ? "active" : ""}`}
                                    onClick={() => setActiveTab("personal")}
                                    type="button"
                                >
                                    <FaUser size={16} /> <span>Personal Info</span>
                                </button>
                            </li>
                            <li className="nav-item flex-fill">
                                <button
                                    className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === "professional" ? "active" : ""}`}
                                    onClick={() => setActiveTab("professional")}
                                    type="button"
                                >
                                    <FaBriefcase size={16} /> <span>Professional</span>
                                </button>
                            </li>
                            <li className="nav-item flex-fill">
                                <button
                                    className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === "family" ? "active" : ""}`}
                                    onClick={() => setActiveTab("family")}
                                    type="button"
                                >
                                    <FaUsers size={16} /> <span>Family & Assets</span>
                                </button>
                            </li>
                            <li className="nav-item flex-fill">
                                <button
                                    className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === "photos" ? "active" : ""}`}
                                    onClick={() => setActiveTab("photos")}
                                    type="button"
                                >
                                    <FaCamera size={16} /> <span>Photos</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* FORM */}
                <form onSubmit={handleSubmit} className="animate-fade-in" style={{ animationDelay: "0.2s" }}>
                    
                    {/* TAB CONTENT: PERSONAL */}
                    {activeTab === "personal" && (
                        <div className="card border-0 shadow-sm p-4 mb-4">
                            <h4 className="fw-bold mb-4 text-primary d-flex align-items-center gap-2">
                                <FaUser /> Personal Information
                            </h4>
                            <div className="row">
                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Full Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        value={form.name}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Mobile Number <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        name="mobile"
                                        className="form-control"
                                        value={form.mobile}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Date Of Birth</label>
                                    <input
                                        type="date"
                                        name="birthday"
                                        className="form-control"
                                        value={form.birthday}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-2 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Age</label>
                                    <input
                                        type="number"
                                        name="age"
                                        className="form-control"
                                        value={form.age}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-5 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Current Address</label>
                                    <input
                                        type="text"
                                        name="address"
                                        className="form-control"
                                        value={form.address}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-5 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Native Place Address</label>
                                    <input
                                        type="text"
                                        name="nativeAddress"
                                        className="form-control"
                                        value={form.nativeAddress}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Marriage Anniversary</label>
                                    <input
                                        type="date"
                                        name="anniversary"
                                        className="form-control"
                                        value={form.anniversary}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>
                            </div>

                            <hr className="my-4 text-muted opacity-25" />

                            <h4 className="fw-bold mb-4 text-danger d-flex align-items-center gap-2">
                                <FaHeart /> Habits & Passions
                            </h4>
                            <div className="row">
                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Hobbies</label>
                                    <input
                                        type="text"
                                        name="hobbies"
                                        className="form-control"
                                        value={form.hobbies}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Dreams & Goals</label>
                                    <input
                                        type="text"
                                        name="dreams"
                                        className="form-control"
                                        value={form.dreams}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Will / Wish</label>
                                    <input
                                        type="text"
                                        name="wishes"
                                        className="form-control"
                                        value={form.wishes}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-6 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Eating Habits</label>
                                    <input
                                        type="text"
                                        name="eatingHabits"
                                        className="form-control"
                                        value={form.eatingHabits}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-6 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Alcohol / Smoking Habits</label>
                                    <input
                                        type="text"
                                        name="alcoholHabits"
                                        className="form-control"
                                        value={form.alcoholHabits}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB CONTENT: PROFESSIONAL */}
                    {activeTab === "professional" && (
                        <div className="card border-0 shadow-sm p-4 mb-4">
                            <h4 className="fw-bold mb-4 text-primary d-flex align-items-center gap-2">
                                <FaBriefcase /> Professional Details
                            </h4>
                            <div className="row">
                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Department <span className="text-danger">*</span></label>
                                    <select
                                        name="department"
                                        className="form-select"
                                        value={form.department}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map((dep) => (
                                            <option key={dep._id} value={dep.name}>
                                                {dep.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Reporting Officer</label>
                                    <input
                                        type="text"
                                        name="reportingOfficer"
                                        className="form-control"
                                        value={form.reportingOfficer}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Office Shift Timing</label>
                                    <input
                                        type="text"
                                        name="officeTime"
                                        className="form-control"
                                        value={form.officeTime}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Transfer / Promotion / Disciplinary History</label>
                                    <textarea
                                        name="transferHistory"
                                        rows="3"
                                        className="form-control"
                                        value={form.transferHistory}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Office Politics / Issues / Observations</label>
                                    <textarea
                                        name="officePolitics"
                                        rows="3"
                                        className="form-control"
                                        value={form.officePolitics}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB CONTENT: FAMILY & ASSETS */}
                    {activeTab === "family" && (
                        <div className="card border-0 shadow-sm p-4 mb-4">
                            <h4 className="fw-bold mb-4 text-primary d-flex align-items-center gap-2">
                                <FaUsers /> Family Details
                            </h4>
                            <div className="row">
                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Total Family Members</label>
                                    <input
                                        type="text"
                                        name="familyMembers"
                                        className="form-control"
                                        value={form.familyMembers}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Spouse / Wife Name</label>
                                    <input
                                        type="text"
                                        name="wife"
                                        className="form-control"
                                        value={form.wife}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Father's Name</label>
                                    <input
                                        type="text"
                                        name="fathers"
                                        className="form-control"
                                        value={form.fathers}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-3 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Father's Occupation</label>
                                    <input
                                        type="text"
                                        name="fathersOccupation"
                                        className="form-control"
                                        value={form.fathersOccupation}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Mother's Name</label>
                                    <input
                                        type="text"
                                        name="mothers"
                                        className="form-control"
                                        value={form.mothers}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Brothers (Names / Count)</label>
                                    <input
                                        type="text"
                                        name="brothers"
                                        className="form-control"
                                        value={form.brothers}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Sisters (Names / Count)</label>
                                    <input
                                        type="text"
                                        name="sisters"
                                        className="form-control"
                                        value={form.sisters}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Children (Names / Ages)</label>
                                    <input
                                        type="text"
                                        name="children"
                                        className="form-control"
                                        value={form.children}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Children School / College Details</label>
                                    <input
                                        type="text"
                                        name="childSchoolCollege"
                                        className="form-control"
                                        value={form.childSchoolCollege}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Studied In Which City</label>
                                    <input
                                        type="text"
                                        name="studiedCity"
                                        className="form-control"
                                        value={form.studiedCity}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>
                            </div>

                            <hr className="my-4 text-muted opacity-25" />

                            <h4 className="fw-bold mb-4 text-primary d-flex align-items-center gap-2">
                                💰 Assets, Views & Health
                            </h4>
                            <div className="row">
                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Financial Assets (Properties, Investments)</label>
                                    <input
                                        type="text"
                                        name="financial"
                                        className="form-control"
                                        value={form.financial}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Financial Stress / Debt Details</label>
                                    <input
                                        type="text"
                                        name="debt"
                                        className="form-control"
                                        value={form.debt}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Vehicle in Use</label>
                                    <input
                                        type="text"
                                        name="vehicle"
                                        className="form-control"
                                        value={form.vehicle}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Social Media Accounts</label>
                                    <input
                                        type="text"
                                        name="socialMedia"
                                        className="form-control"
                                        value={form.socialMedia}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Political Views / Leanings</label>
                                    <input
                                        type="text"
                                        name="politicalView"
                                        className="form-control"
                                        value={form.politicalView}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-4 mb-3">
                                    <label className="form-label text-muted small fw-semibold">Medical Condition / Allergies</label>
                                    <input
                                        type="text"
                                        name="medicalCondition"
                                        className="form-control"
                                        value={form.medicalCondition}
                                        onChange={handleChange}
                                        disabled={viewOnly}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB CONTENT: PHOTOS */}
                    {activeTab === "photos" && (
                        <div className="card border-0 shadow-sm p-4 mb-4">
                            <h4 className="fw-bold mb-4 text-primary d-flex align-items-center gap-2">
                                <FaCamera /> Uploaded Media
                            </h4>
                            
                            {!viewOnly && (
                                <div className="image-upload-zone position-relative mb-4">
                                    <input
                                        type="file"
                                        multiple
                                        className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                                        onChange={handleImages}
                                        style={{ zIndex: 5 }}
                                    />
                                    <div className="py-4">
                                        <FaCamera className="text-muted mb-3" size={48} />
                                        <h5 className="fw-bold">Drag and drop new images here</h5>
                                        <p className="text-muted small mb-0">or click to browse from your computer (Multiple files allowed)</p>
                                    </div>
                                </div>
                            )}

                            {/* PREVIEW IMAGES */}
                            {previewImages.length > 0 && (
                                <div>
                                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Images ({previewImages.length})</h6>
                                    <div className="row g-3">
                                        {previewImages.map((img, index) => (
                                            <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={index}>
                                                <div className="card border-0 shadow-sm h-100 overflow-hidden" style={{ borderRadius: "12px" }}>
                                                    <img
                                                        src={img}
                                                        alt={`preview-${index}`}
                                                        className="img-fluid"
                                                        style={{
                                                            height: "140px",
                                                            objectFit: "cover"
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* NAVIGATION & SUBMIT BUTTONS */}
                    <div className="d-flex justify-content-between align-items-center p-3 card border-0 shadow-sm flex-row">
                        <div>
                            {activeTab !== "personal" && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                    onClick={() => {
                                        if (activeTab === "professional") setActiveTab("personal");
                                        else if (activeTab === "family") setActiveTab("professional");
                                        else if (activeTab === "photos") setActiveTab("family");
                                    }}
                                >
                                    <FaArrowLeft size={12} /> Previous
                                </button>
                            )}
                        </div>

                        <div className="d-flex gap-2">
                            {activeTab !== "photos" ? (
                                <button
                                    type="button"
                                    className="btn btn-dark d-flex align-items-center gap-2"
                                    onClick={() => {
                                        if (activeTab === "personal") setActiveTab("professional");
                                        else if (activeTab === "professional") setActiveTab("family");
                                        else if (activeTab === "family") setActiveTab("photos");
                                    }}
                                >
                                    Next <FaArrowRight size={12} />
                                </button>
                            ) : (
                                !viewOnly && (
                                    <button
                                        type="submit"
                                        className="btn btn-primary d-flex align-items-center gap-2"
                                        disabled={loading}
                                        style={{ boxShadow: "0 4px 12px rgba(79, 70, 229, 0.25)" }}
                                    >
                                        <FaSave size={14} /> {loading ? "Updating..." : "Update Profile"}
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </form>

                {/* HISTORY TIMELINE (ADMIN ONLY) */}
                {localStorage.getItem("role") === "admin" && historyLogs.length > 0 && (
                    <div className="card border-0 shadow-sm p-4 mt-4 animate-fade-in" style={{ animationDelay: "0.3s" }}>
                        <h4 className="fw-bold mb-4 text-primary d-flex align-items-center gap-2">
                            📋 Profile History Logs
                        </h4>
                        <div className="position-relative ps-4" style={{ borderLeft: "2px solid var(--border-light)" }}>
                            {historyLogs.map((log) => (
                                <div key={log._id} className="position-relative mb-4">
                                    {/* Bullet */}
                                    <div 
                                        className="position-absolute rounded-circle" 
                                        style={{ 
                                            left: "-25px", 
                                            top: "4px", 
                                            width: "10px", 
                                            height: "10px", 
                                            backgroundColor: "var(--primary-color)",
                                            border: "2px solid #ffffff",
                                            boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.2)"
                                        }}
                                    />
                                    <div className="fw-bold small text-dark">
                                        {log.action} by {log.performedByName} 
                                        <span className="text-muted fw-normal font-monospace ms-2 small">
                                            ({log.performedByEmail || "System"})
                                        </span>
                                    </div>
                                    <div className="text-slate small mt-1">{log.details}</div>
                                    <div className="text-muted font-monospace mt-1" style={{ fontSize: "11px" }}>
                                        {new Date(log.createdAt).toLocaleString()} | IP: {log.ipAddress}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                    </>
                )}
            </div>
        </div>
    );
}

export default EditProfile;